import { useLayoutEffect, useEffect, useRef, useState, useMemo, useCallback, type PropsWithChildren } from 'react';
import Icon, { ICON_RIGHT, ICON_LEFT } from '~/components/icon/index';
import { CarouselWrapper, CarouselScroller, ArrowLeft, ArrowRight, CarouselControls } from './category-carousel.styles';

type CategoryCarouselProps = {
	$alignSelf?: React.CSSProperties['alignSelf'];
	$scrollerPadding?: React.CSSProperties['padding'];
	disableEndGap?: boolean;
	childrenWidth?: number;
};

const CategoryCarousel = ({
	children,
	$alignSelf = 'unset',
	$scrollerPadding = '4px',
	disableEndGap = false,
	childrenWidth = undefined
}: PropsWithChildren<CategoryCarouselProps>) => {
	const carouselRef = useRef<HTMLDivElement | null>(null);
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const childrenRef = useRef<HTMLDivElement | null>(null);

	const [screenWidth, setScreenWidth] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);

	const [contentWidth, setContentWidth] = useState<number>(childrenWidth || childrenRef?.current?.clientWidth || 0);

	useLayoutEffect(() => {
		const width = childrenRef.current?.clientWidth;

		if (width && !childrenWidth) {
			setContentWidth(width);
		}
	}, [childrenRef, childrenWidth]);

	const scrollState = useMemo(() => ({ left: 0, positionX: 0 }), []);

	useEffect(() => {
		if (carouselRef.current) setScreenWidth(carouselRef?.current?.clientWidth);
	}, [carouselRef?.current?.clientWidth]);

	const elementMargin = 12;

	useLayoutEffect(() => {
		const currentScrollerRef = scrollerRef.current;

		if (!currentScrollerRef) return;

		const listenScrollPosition = () => {
			setScrollLeft(currentScrollerRef.scrollLeft);
		};

		currentScrollerRef.addEventListener('scroll', listenScrollPosition);

		return () => currentScrollerRef.removeEventListener('scroll', listenScrollPosition);
	}, [scrollerRef]);

	const moveCarousel = (direction: 'right' | 'left') => () => {
		if (!scrollerRef.current) return;

		const elementWidth = childrenRef.current?.firstElementChild?.firstElementChild?.clientWidth ?? 0;

		const moveCarouselPosition = screenWidth % (elementWidth + elementMargin);

		const setNewPosition = screenWidth > moveCarouselPosition ? screenWidth - moveCarouselPosition : elementWidth;

		scrollerRef.current.scrollTo({
			left:
				direction === 'right'
					? scrollerRef.current.scrollLeft + setNewPosition
					: scrollerRef.current.scrollLeft - setNewPosition,
			behavior: 'smooth'
		});
	};

	const mouseMoveHandler = useCallback(
		(e: MouseEvent) => {
			const deltaX = e.clientX - scrollState.positionX;

			if (!scrollerRef.current) return;
			scrollerRef.current.scrollLeft = scrollState.left - deltaX;
		},
		[scrollState, scrollerRef]
	);

	const mouseUpHandler = useCallback(() => {
		if (!scrollerRef.current) return;

		scrollerRef.current.removeEventListener('mousemove', mouseMoveHandler);
		window.removeEventListener('mouseup', mouseUpHandler);

		scrollerRef.current.style.cursor = 'default';
		scrollerRef.current.style.removeProperty('user-select');
	}, [scrollerRef, mouseMoveHandler]);

	const mouseDownHandler = useCallback(
		(e: MouseEvent) => {
			if (e.target instanceof Element && e.target.nodeName === 'BUTTON') return;

			if (!scrollerRef.current) return;

			scrollerRef.current.style.cursor = 'grabbing';
			scrollerRef.current.style.userSelect = 'none';

			scrollState.left = scrollerRef.current.scrollLeft;
			scrollState.positionX = e.clientX;

			scrollerRef.current.addEventListener('mousemove', mouseMoveHandler);
			window.addEventListener('mouseup', mouseUpHandler);
		},
		[scrollerRef, mouseMoveHandler, mouseUpHandler, scrollState]
	);

	useEffect(() => {
		const currentScrollerRef = scrollerRef.current;

		if (!currentScrollerRef) return;

		currentScrollerRef.addEventListener('mousedown', mouseDownHandler);

		return () => currentScrollerRef.removeEventListener('mousedown', mouseDownHandler);
	}, [mouseDownHandler]);

	return (
		<CarouselWrapper ref={carouselRef} $alignSelf={$alignSelf}>
			<CarouselControls>
				{scrollLeft > 0 && (
					<ArrowLeft onClick={moveCarousel('left')} data-at="carousel-left-arrow">
						<Icon type={ICON_LEFT} />
					</ArrowLeft>
				)}
				{scrollLeft + screenWidth < contentWidth && (
					<ArrowRight onClick={moveCarousel('right')} data-at="carousel-right-arrow">
						<Icon type={ICON_RIGHT} />
					</ArrowRight>
				)}
			</CarouselControls>
			<CarouselScroller ref={scrollerRef} $scrollerPadding={$scrollerPadding} disableEndGap={disableEndGap}>
				<div ref={childrenRef}>{children}</div>
			</CarouselScroller>
		</CarouselWrapper>
	);
};

export default CategoryCarousel;
